import CommentForm from '@/components/Messages/CommentForm'
import Row from '@/components/Messages/Row'
import useInterval from '@/hooks/useInterval'
import PropTypes from 'prop-types'
import React, {
  useCallback, useEffect, useLayoutEffect, useRef, useState,
} from 'react'
import { LoadManager } from '@/components'
import { ChatBody, ChatList } from '../styles'

function normalizeHistory(rows) {
  return (rows || []).map((message) => ({
    ...message,
    attachmentURL: message.attachmentURL || message.images?.[0],
  }))
}

function messageSentByPartner(message, licensing) {
  return message?.personId === licensing?.licensedId
    || message?.authorId === licensing?.licensedId
}

function Chat({
  selected, messageService, onRead, onMessageAdded, licensing,
}) {
  const [history, setHistory] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const listRef = useRef()
  const endListRef = useRef()
  const scrollBottomRef = useRef(false)
  const selectedRef = useRef()
  const lastMessageIdRef = useRef()
  const onMessageAddedRef = useRef()
  const selectedId = selected?.conversationId
  const closed = selected?.type === 'adoption' && selected?.closed

  useEffect(() => {
    selectedRef.current = selected
  }, [selected])

  useEffect(() => {
    onMessageAddedRef.current = onMessageAdded
  }, [onMessageAdded])

  const fetchHistory = useCallback(async (showLoading = false, updateLastMessage = false) => {
    const currentSelected = selectedRef.current
    if (!currentSelected) return

    if (showLoading) setLoading(true)
    try {
      const response = await messageService.history(currentSelected)
      const rows = normalizeHistory(response.data)
      const lastMessage = rows[rows.length - 1]
      const lastMessageChanged = lastMessage?.id && lastMessage.id !== lastMessageIdRef.current

      if (updateLastMessage && lastMessageChanged) {
        onMessageAddedRef.current(lastMessage)
      }

      lastMessageIdRef.current = lastMessage?.id
      scrollBottomRef.current = (
        showLoading
        || !lastMessageIdRef.current
        || Boolean(lastMessageChanged)
      )
      setHistory(rows)
      setError(null)
    } catch (requestError) {
      setError(requestError)
    } finally {
      if (showLoading) setLoading(false)
    }
  }, [messageService])

  function handleMessageAdded(message) {
    if (message?.id) {
      lastMessageIdRef.current = message.id
      scrollBottomRef.current = true
      setHistory((current) => (
        current.some((item) => item.id === message.id)
          ? current
          : [...current, ...normalizeHistory([message])]
      ))
    } else {
      fetchHistory()
    }
    onMessageAddedRef.current(message)
  }

  useEffect(() => {
    setHistory([])
    setError(null)
    lastMessageIdRef.current = null
    if (selectedId) {
      fetchHistory(true)
      onRead(selectedRef.current)
    }
  }, [fetchHistory, onRead, selectedId])

  useInterval(() => fetchHistory(false, true), selected ? 5000 : null)

  function scrollToBottom() {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight
    }
    endListRef.current?.scrollIntoView({ block: 'end' })
  }

  useLayoutEffect(() => {
    if (!history.length || loading || !scrollBottomRef.current) return undefined

    const animationFrame = requestAnimationFrame(scrollToBottom)
    const timeout = setTimeout(scrollToBottom, 100)
    scrollBottomRef.current = false

    return () => {
      cancelAnimationFrame(animationFrame)
      clearTimeout(timeout)
    }
  }, [history.length, loading])

  return (
    <LoadManager loading={loading} error={error}>
      <ChatBody>
        <ChatList ref={listRef}>
          {
            history.length === 0
            && (
              <div className="empty mb-2">
                <div className="empty-icon">
                  <i className="fa-3x fas fa-envelope-open" />
                </div>
                <p className="empty-title h5">Conversa vazia</p>
                <p className="empty-subtitle">Ainda não há mensagens nesta conversa</p>
              </div>
            )
          }
          {
            history.map((message) => (
              <Row
                key={message.id}
                comment={message}
                providerId={licensing?.licensedId}
                messageClassName={
                  messageSentByPartner(message, licensing)
                    ? 'message-sent'
                    : 'message-received'
                }
              />
            ))
          }
          <span ref={endListRef} />
        </ChatList>
        <div className="divider" />
        {
          closed
            ? <div className="text-center text-gray p-2">Esta conversa foi encerrada.</div>
            : (
              <CommentForm
                addMessage={(body) => messageService.addMessage(selected, body)}
                onCommentAdded={handleMessageAdded}
              />
            )
        }
      </ChatBody>
    </LoadManager>
  )
}

Chat.propTypes = {
  selected: PropTypes.objectOf(PropTypes.any).isRequired,
  messageService: PropTypes.objectOf(PropTypes.func).isRequired,
  onRead: PropTypes.func.isRequired,
  onMessageAdded: PropTypes.func.isRequired,
  licensing: PropTypes.objectOf(PropTypes.any),
}

Chat.defaultProps = {
  licensing: null,
}

export default Chat
